<template>
  <NuxtLayout
    v-if="destination"
    name="charter-destination"
  >
    <div class="charter-destination pb-12">
      <div class="container mx-auto top-section z-[1] mb-4">
        <div
          v-if="currentTabComponent"
          class="flex flex-wrap"
        >
          <component
            :is="currentTabComponent"
            :destination="destination"
            :destination-id="destination?.destination_id"
            :filled-sections="filledSections"
          />
        </div>
      </div>
    </div>
  </NuxtLayout>
</template>

<script setup>
import DestinationInfoTab from '@/components/destination/tabs/DestinationInfoTab.vue'
import DestinationHotelTab from '@/components/destination/tabs/DestinationHotelTab.vue'
import DestinationWeatherTab from '@/components/destination/tabs/DestinationWeatherTab.vue'
import DestinationBeachesTab from '@/components/destination/tabs/DestinationBeachesTab.vue'
import DestinationAreaTab from '@/components/destination/tabs/DestinationAreaTab.vue'

const { t } = useI18n()

const { $sentryCaptureException } = useNuxtApp()

const { urls: localeURLs } = useLocale()
const route = useRoute()

const dealfinderStore = useDealfinderStore()
const charterPackagesStore = useCharterPackagesStore()
const charterDestinationStore = useCharterDestinationStore()
const rootStore = useRootStore()
const charterStore = useCharterStore()
const router = useRouter()

const { packages } = storeToRefs(charterPackagesStore)
const { charterAirports } = storeToRefs(rootStore)

const activeTab = computed(() => charterDestinationStore.charterDestinationTab)

const currentTabComponent = computed(() => {
  switch (activeTab.value) {
    case 'info':
      return DestinationInfoTab
    case 'hotel':
      return DestinationHotelTab
    case 'weather':
      return DestinationWeatherTab
    case 'beaches':
      return DestinationBeachesTab
    case 'area':
      return DestinationAreaTab
    default:
      return null
  }
})

const { $isSol } = useNuxtApp()

const { error, data } = await useAsyncData(async () => {
  if (!$isSol) {
    return navigateTo('/')
  }

  try {
    const tripName = encodeURIComponent(route.params.id)
    const tripUrlId = `/destination/${tripName}`

    dealfinderStore.SET_HOTEL(null)
    charterPackagesStore.SET_PACKAGES([])

    const [result] = await Promise.all([
      charterStore.fetchDestination({ destination: route.params.id }),
      rootStore.fetchCharterAirports(),
      rootStore.fetchCharterDestinations(),
      charterPackagesStore.getDestinations(),
      charterPackagesStore.getFilters(),
      charterDestinationStore.SET_LOADING(true),
    ])

    const activeTab = findActiveSlugKey(localeURLs.destinationsId, route.params)
    charterDestinationStore.SET_CHARTER_DESTINATION_TAB(activeTab)

    rootStore.SET_PAGE_BANNER({
      background: {
        type: 'cloudinary',
        image: {
          url: result.image_cloudinary,
        },
      },
    })

    charterDestinationStore.SET_CHARTER_DESTINATION(result)
    charterDestinationStore.SET_CHARTER_DESTINATION_SECTIONS(result.sections)
    charterDestinationStore.SET_CHARTER_DESTINATION_TITLE(result.title)
    charterDestinationStore.SET_CHARTER_DESTINATION_TABS(result.sections.map(s => s.type))
    charterDestinationStore.SET_CHARTER_DESTINATION_PATH(tripUrlId)

    return {
      destination: result,
      sections: result.sections,
    }
  } catch (e) {
    $sentryCaptureException(e)
    throw new Error('Post not found: ' + router.currentRoute.value.fullPath)
  }
})

if (error.value) {
  throw createError({
    message: error.value.message,
    statusCode: 404,
    stack: error.value.stack,
    fatal: true,
  })
}

const { destination, sections } = data.value

useHead({
  title: () => `${destination.title}`,
  link: [
    {
      rel: 'canonical',
      href: () => `${localeURLs.BASE_URL}${localeURLs.destinations}/${route.params.id}`,
    },
  ],

  meta: [
    { hid: 'author', name: 'author', content: () => t('siteTitleBrand') },
    {
      hid: 'og:url',
      property: 'og:url',
      content: () => `${localeURLs.BASE_URL}${localeURLs.destinations}/${route.params.id}`,
    },
    {
      hid: 'og:title',
      property: 'og:title',
      content: () => `${destination.title} - ${t('siteTitleBrand')}`,
    },
    {
      hid: 'og:image',
      property: 'og:image',
      content: () => formatCloudinaryImagePath(destination.image_cloudinary, {
        transformation: { watermark: true },
        forceJPG: true,
        isSol: true,
      }),
    },
    {
      hid: 'og:description',
      name: 'og:description',
      content: () => trimByWord(sections?.find(s => s.type === 'destination_summary')?.data.preamble),
    },
    {
      hid: 'description',
      name: 'description',
      content: () => trimByWord(sections?.find(s => s.type === 'destination_summary')?.data.preamble),
    },
  ],
})

const filledSections = computed(() => {
  const secs = deepClone(sections || [])
  const dealfinder = secs.find(s => s.type === 'destination_dealfinder')
  if (dealfinder) {
    dealfinder.data = transformDealfinderData()
  }

  return secs
})

onMounted(() => {
  window.scrollTo(0, 0)
  charterDestinationStore.SET_LOADING(false)
})

onUnmounted(() => {
  charterStore.SET_DESTINATION({})
})

// refactor this to dealfinder store
const transformDealfinderData = () => {
  const defaultCustomValue = {
    type: 'custom',
    value: 'custom',
    custom: [2, 30],
    maxValue: 365,
  }
  const transformPackageDate = p => ({
    ...p,
    price: p.pricepp,
    return: p.return_date,
  })
  const transformDayGroup = dg => ({
    ...dg,
    value: dg?.min,
    maxValue: dg?.max,
    nameValue: dg?.name,
  })
  const transformTripLength = d => ({
    ...d,
    name: d.value,
    max: d.maxValue,
    type: d.type,
    dates: packages.value
      .filter(p => filterPackageDate(p, d))
      .map(transformPackageDate),
  })
  const filterPackageDate = (p, d) => {
    if (d.type === 'custom') {
      return p.days >= d.custom[0] && p.days <= d.custom[1]
    }
    return p.days >= d.value && p.days <= d.maxValue
  }

  const newSchool = !destination?.daygroups && destination?.dayGroupsByOrigin
  if (newSchool) {
    return Object.entries(destination?.dayGroupsByOrigin || {})
      .map(([airport, dayGroups]) => {
        const durations = [
          ...(dayGroups || []).map(transformDayGroup),
          defaultCustomValue,
        ]
        return {
          airport,
          triplengths: durations
            .map(transformTripLength),
        }
      })
  }

  const durations = [
    ...(destination?.daygroups || []).map(transformDayGroup),
    defaultCustomValue,
  ]

  return charterAirports.value.map(({ airport }) => ({
    airport,
    triplengths: durations.map(transformTripLength),
  }))
}
</script>

<style lang="scss">
@import "@layers/web/assets/scss/modules/charter/_charter-destination";
</style>
